import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, SHAPES } from '../gameData';

export default class TreesArea extends RegionArea
{
    /**
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[parentRegion.region].TREES);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.cone.setAlpha(0);
        this.shrub.setAlpha(0);
        this.tree.setAlpha(0);
        this.treeTrunk1.setAlpha(0);
        this.treeTrunk2.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.cone.disableInteractive();
        this.shrub.disableInteractive();
        this.tree.disableInteractive();
        this.treeTrunk1.disableInteractive();
        this.treeTrunk2.disableInteractive();
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.cone.setAlpha(0.000001);
        this.shrub.setAlpha(0.000001);
        this.tree.setAlpha(0.000001);
        this.treeTrunk1.setAlpha(0.000001);
        this.treeTrunk2.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        let coneHitArea = new Phaser.Geom.Polygon(
            [
                { x: 123, y: 393 },
                { x: 41, y: 374 },
                { x: 0, y: 321 },
                { x: 123, y: 0 },
                { x: 245, y: 321 },
                { x: 204, y: 374 },
            ]);
        this.cone = ABCya.Images.addImage(584, 456, UIAssetKeys._10_Camping_Zoom_In_Trees_Slct_Cone, this.parentRegion).setInteractive(coneHitArea, Phaser.Geom.Polygon.Contains);
        this.cone.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.cone, SHAPES.CONE);
        });

        this.shrub = ABCya.Images.addImage(732, 645, UIAssetKeys._10_Camping_Zoom_In_Trees_Slct_Shrb, this.parentRegion);
        this.shrub.setInteractive(new Phaser.Geom.Circle(this.shrub.width / 2, this.shrub.height / 2, this.shrub.width / 2), Phaser.Geom.Circle.Contains);
        this.shrub.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.shrub, SHAPES.SPHERE);
        });

        this.tree = ABCya.Images.addImage(717, 258, UIAssetKeys._10_Camping_Zoom_In_Trees_Slct_Tree1, this.parentRegion);
        this.tree.setInteractive(new Phaser.Geom.Circle(this.tree.width / 2, this.tree.height / 2, this.tree.width / 2), Phaser.Geom.Circle.Contains);
        this.tree.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.tree, SHAPES.SPHERE);
        });

        let treeTrunk1HitArea = new Phaser.Geom.Polygon(
            [
                { x: 22, y: 392 },
                { x: 0, y: 383 },
                { x: 0, y: 11 },
                { x: 22, y: 0 },
                { x: 43, y: 11 },
                { x: 43, y: 383 },
            ]);
        this.treeTrunk1 = ABCya.Images.addImage(717, 450, UIAssetKeys._10_Camping_Zoom_In_Trees_Slct_Tree1_Trnk, this.parentRegion).setInteractive(treeTrunk1HitArea, Phaser.Geom.Polygon.Contains);
        this.treeTrunk1.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.treeTrunk1, SHAPES.CYLINDER);
        });

        let treeTrunk2HitArea = new Phaser.Geom.Polygon(
            [
                { x: 22, y: 94 },
                { x: 1, y: 86 },
                { x: 1, y: 9 },
                { x: 22, y: 0 },
                { x: 42, y: 11 },
                { x: 42, y: 86 },
            ]);
        this.treeTrunk2 = ABCya.Images.addImage(581, 652, UIAssetKeys._10_Camping_Zoom_In_Trees_Slct_Tree2_Trnk, this.parentRegion).setInteractive(treeTrunk2HitArea, Phaser.Geom.Polygon.Contains);
        this.treeTrunk2.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.treeTrunk2, SHAPES.CYLINDER);
        });

        this.shapeOverlays[SHAPES.CONE] = [this.cone];
        this.shapeOverlays[SHAPES.SPHERE] = [this.shrub, this.tree];
        this.shapeOverlays[SHAPES.CYLINDER] = [this.treeTrunk1, this.treeTrunk2];

        this._createAreaInformation(0x5f5fd6, 0x204243);
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.tent, this.parentRegion.truck, this.parentRegion.chair
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.treeAreaContainer, duration: 1000, props: { scale: 1.84196, x: 153, y: -107 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 }
        );

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.cone.disableInteractive();
        this.shrub.disableInteractive();
        this.tree.disableInteractive();
        this.treeTrunk1.disableInteractive();
        this.treeTrunk2.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.treeAreaContainer, duration: 1000, props: { scale: 1, x: 0, y: 0 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.tent, this.parentRegion.chair, this.parentRegion.truck], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
        );

        return tweens;
    }
}